import {
  Code,
  CodeDeck,
  CodeDeckPost,
  CodeDeckPut,
  CodePost,
  CodePut,
} from "callable-data-objects";
import { APICodeDeckContext } from "Providers/APIContexts";
import { APICodeDeckProps, DefaultProviderProps } from "Providers/types";
import React, { useEffect, useState } from "react";
import { useAPI } from "./APIProvider";

const APICodeDeckProvider = ({ children }: DefaultProviderProps) => {
  const api = useAPI();
  const [codeDecks, setCodeDecks] = useState<CodeDeck[]>([]);
  const [codeDeck, setCodeDeck] = useState<CodeDeck | undefined>();
  const [code, setCode] = useState<Code | undefined>();
  const [create, setCreate] = useState<CodeDeckPost | undefined>();
  const [createCode, setCreateCode] = useState<CodePost | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<undefined | string>("");
  const [searchCode, setSearchCode] = useState<undefined | string>("");

  async function list() {
    setLoading(true);
    const response = await api.codeDeckService.list();
    setCodeDecks(response);
    setLoading(false);
  }

  useEffect(() => {
    list();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  function select(id: string): void {
    setLoading(true);
    api.codeDeckService.get(id).then((resp: CodeDeck) => {
      setCodeDeck(resp);
      setLoading(false);
    });
  }

  function selectCode(id: string): void {
    if (codeDeck) {
      const r = codeDeck.codes.find((r) => r.id === id);
      setCode(r);
    }
  }

  function unselect() {
    setCodeDeck(undefined);
    setCode(undefined);
  }

  function unselectCode() {
    setCode(undefined);
  }

  function createCodeDeck() {
    setCreate({
      name: "",
      description: "",
    });
  }

  function createCodeDeckCode() {
    setCreateCode({
      caller: "*",
      called: "",
      code: "",
      category: "",
      country: "",
      countryCode: "",
    });
  }

  function uncreate() {
    setCreate(undefined);
    setCreateCode(undefined);
  }

  function uncreateCode() {
    setCreateCode(undefined);
  }

  function validateCodeDeckName(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Name required";
    if (value.length < 3) error = "Name must be 3 or more characters";
    if (
      codeDecks
        .filter((rt) => codeDeck && codeDeck.id !== rt.id)
        .map((rt) => rt.name)
        .indexOf(value) > -1
    ) {
      error = "Name not unique";
    }
    return error;
  }

  async function save(payload: CodeDeckPost) {
    if (create) {
      const response = await api.codeDeckService.post("", payload);
      if (response) {
        list();
        select(response.id);
        uncreate();
      }
    }
  }

  async function saveCode(payload: CodePost) {
    if (createCode && codeDeck) {
      const response = await api.codeDeckService
        .code(codeDeck.id)
        .post("", payload);
      if (response) {
        selectCode(response.id);
      }
    }
  }

  async function update(payload: CodeDeckPut) {
    if (codeDeck) {
      await api.codeDeckService.put(payload.id, payload);
      list();
      select(payload.id);
    }
  }

  async function remove(id: string) {
    await api.codeDeckService.delete(id);
    list();
    unselect();
  }

  async function removeCode(id: string) {
    if (codeDeck) {
      codeDeck.codes = codeDeck.codes.filter((r) => r.id !== id);
      update(codeDeck);
    }
  }

  async function cloneCodeDeck() {
    if (codeDeck) {
      const response = await api.codeDeckService.clone(codeDeck);
      list();
      if (response instanceof CodeDeck) setCodeDeck(response);
    }
  }

  async function updateCode(payload: CodePut) {
    if (codeDeck && code) {
      await api.codeDeckService
        .code(codeDeck.id)
        .put(payload.id, payload)
        .then(() => {
          setCode(undefined);
          select(codeDeck.id);
        });
    }
  }

  const codeDeckProps: APICodeDeckProps = {
    code,
    codeDecks,
    codeDeck,
    create,
    createCode,
    loading,
    save,
    saveCode,
    update,
    updateCode,
    validateCodeDeckName,
    select,
    selectCode,
    unselect,
    unselectCode,
    createCodeDeck,
    createCodeDeckCode,
    uncreate,
    uncreateCode,
    search,
    setSearch,
    searchCode,
    setSearchCode,
    cloneCodeDeck,
    remove,
    removeCode,
  };

  return (
    <APICodeDeckContext.Provider value={codeDeckProps}>
      {children}
    </APICodeDeckContext.Provider>
  );
};

export default APICodeDeckProvider;
